import { supabase } from '../supabaseClient';
import {
  computeStaleSchoolDataDomains,
  isAffectedBySchoolDataDomains,
  normalizeSchoolDataRevisions,
  SCHOOL_DATA_DOMAINS,
  schoolCacheKeyDomain,
  type SchoolDataDomain,
  type SchoolDataRevisions,
} from './schoolDataDomains';
import { deviceCacheStorage } from './deviceCacheStorage';

const SCHOOL_DATA_CACHE_VERSION_KEY_PREFIX = 'cp.schoolDataVersion.v1.';
const SCHOOL_DATA_NEEDS_RELOAD_KEY_PREFIX = 'cp.schoolDataNeedsReload.v1.';
/** 이 기기가 마지막으로 맞춘 파트별 revision(`workspaces.data_revisions`) */
const SCHOOL_DATA_REVISIONS_KEY_PREFIX = 'cp.schoolDataRevisions.v1.';

/** 파트별 동기화 결과: 'all'이면 전체 purge, 배열이면 해당 파트(+미분류 파생 캐시)만 purge */
export type SchoolDataChangedDomains = 'all' | SchoolDataDomain[];

export type SchoolDataSyncResult = {
  isFresh: boolean;
  changedDomains: SchoolDataChangedDomains;
};

/** localStorage 키에 버전 세그먼트(v1 등)가 들어가 prefix+workspaceId 방식으로는 절대 매칭되지 않음 → workspaceId 구간으로 판별 */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function isPreservedMetaKey(key: string): boolean {
  return (
    key.startsWith(SCHOOL_DATA_CACHE_VERSION_KEY_PREFIX) ||
    key.startsWith(SCHOOL_DATA_NEEDS_RELOAD_KEY_PREFIX) ||
    key.startsWith(SCHOOL_DATA_REVISIONS_KEY_PREFIX) ||
    key === 'curriculumplanner-school-id' ||
    key === 'curriculumplanner-grade-id' ||
    key === 'curriculum-theme'
  );
}

const syncedSchoolIds = new Set<string>();
const pendingSyncBySchoolId = new Map<string, Promise<SchoolDataSyncResult>>();

function versionStorageKey(workspaceId: string): string {
  return `${SCHOOL_DATA_CACHE_VERSION_KEY_PREFIX}${workspaceId}`;
}

function needsReloadStorageKey(workspaceId: string): string {
  return `${SCHOOL_DATA_NEEDS_RELOAD_KEY_PREFIX}${workspaceId}`;
}

function revisionsStorageKey(workspaceId: string): string {
  return `${SCHOOL_DATA_REVISIONS_KEY_PREFIX}${workspaceId}`;
}

function keyContainsSchoolIdSegment(key: string, workspaceId: string): boolean {
  const id = escapeRegExp(workspaceId);
  return new RegExp(`(^|[.:])${id}([.:]|$)`).test(key);
}

/** 계정·이용권 스코프(유저 id). workspace id = user id 인 모델에서 학교 캐시 purge에 휩쓸리지 않게 제외 */
function isAccountScopedPassKey(key: string): boolean {
  return key.startsWith('cp.subscriptionPass.') || key === 'cp.subscriptionPass.demo.v1';
}

/**
 * `updated_at` 비교: 클라이언트가 쓴 `...123Z`와 DB 조회 결과 `...123+00:00`은 같은 시각이므로 시각 값으로 비교한다.
 */
export function isSameSchoolDataVersion(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isFinite(ta) && Number.isFinite(tb) && ta === tb;
}

/** LS에 저장된 학교 데이터 버전(`workspaces.updated_at`) */
export function readStoredSchoolDataVersion(workspaceId: string): string | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(versionStorageKey(workspaceId));
  } catch {
    return null;
  }
}

/** null = 이 기기에서 아직 파트별 revision으로 동기화한 적 없음 */
export function readStoredSchoolDataRevisions(workspaceId: string): SchoolDataRevisions | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(revisionsStorageKey(workspaceId));
    if (raw == null) return null;
    return normalizeSchoolDataRevisions(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeStoredSchoolDataRevisions(workspaceId: string, revisions: SchoolDataRevisions | null): void {
  if (!workspaceId || typeof window === 'undefined') return;
  try {
    if (revisions) {
      window.localStorage.setItem(revisionsStorageKey(workspaceId), JSON.stringify(revisions));
      return;
    }
    window.localStorage.removeItem(revisionsStorageKey(workspaceId));
  } catch {
    // ignore localStorage failures
  }
}

/**
 * 방금 이 기기가 올린 파트만 로컬 revision에 반영(다른 파트는 원격을 보지 않은 채로 둔다).
 * bump 직전 서버 값(previous)이 로컬 기록과 다르면 그 사이 다른 기기가 같은 파트를 바꾼 것 → 올리지 않아 다음 동기화에서 다시 받게 한다.
 */
function patchStoredSchoolDataRevisions(
  workspaceId: string,
  bumped: SchoolDataRevisions,
  previous: SchoolDataRevisions
): void {
  const current = readStoredSchoolDataRevisions(workspaceId);
  if (current == null) return;
  const next = { ...current };
  (Object.keys(bumped) as SchoolDataDomain[]).forEach((domain) => {
    if ((previous[domain] ?? '') !== (current[domain] ?? '')) return;
    const rev = bumped[domain];
    if (rev) next[domain] = rev;
  });
  writeStoredSchoolDataRevisions(workspaceId, next);
}

function isSchoolScopedCacheKey(key: string, workspaceId: string): boolean {
  if (!key || !workspaceId || isPreservedMetaKey(key)) return false;
  /** 이용권 LS: 키가 userId를 포함해 workspaceId와 겹치므로 학교 데이터 invalidate에서 제외 */
  if (isAccountScopedPassKey(key)) return false;
  /**
   * 연간 프로젝트 목록은 삭제 대상에서 제외(삭제 시 사이드바가 비고 needsReload로 재캐시도 막힘).
   * 대신 원격에서 annualProjects 파트가 바뀌면 `markAnnualProjectProgramsCachesStale`로 교체 대상 표시.
   */
  if (key.startsWith('cp.annualProjectPrograms.')) return false;
  /** 주간학습안내 테이블 부재 시절의 로컬 전용 원본 — 서버 사본이 없으므로 절대 비우지 않음 */
  if (key.startsWith('cp.weeklyLearningGuideNotes.local.')) return false;
  if (key.startsWith('cp.weeklyLearningGuideTrackColors.local.')) return false;
  /** 편제 페이지의 사용자 드래그 순서(local UI preference)는 워크스페이스 데이터 invalidate에서도 유지 */
  if (key.startsWith('cp.sectionColumnOrder.')) return false;
  if (key.startsWith('cp.trackOrder.')) return false;
  if (key.startsWith('cp.trackAliasOrder.')) return false;
  /**
   * 편제별 프로그램 모드(통합/시간표별/동기화)·대표 시간표·시수조정: 서버 prefs의 LS 사본.
   * 지우면 서버 재조회 전까지 '통합'으로 풀려 보이므로 유지하고, prefs가 바뀌면 서버 값으로 엄격 재시드한다.
   */
  if (key.startsWith('cp.trackProgramMode.')) return false;
  if (key.startsWith('cp.trackProgramSyncRep.')) return false;
  if (key.startsWith('cp.trackProgramSplit.')) return false;
  if (key.startsWith('cp.trackHoursAdjustSettings.')) return false;
  if (key.startsWith('cp.trackHoursTermScope.')) return false;
  /** 연간시간표 보기 토글(토요일·전 클래스·학기 위치) — 데이터 캐시가 아님 */
  if (key.startsWith('cp.annualSwapTermOrder.v1.')) return false;
  if (key.startsWith('cp.annualShowSaturday.v1.')) return false;
  if (key.startsWith('cp.annualShowAllClasses.v1.')) return false;
  /** 멤버 사진 프로필 표시 ON/OFF — 사용자 선호, invalidate·캐시 정리에서 유지 */
  if (key.startsWith('cp-member-profile-display-mode:')) return false;
  /** 학급 포인트 게이지 보기 모드 — 기기별 UI 선호 */
  if (key.startsWith('cp-class-point-gauge-mode:')) return false;
  /** 메인 프레젠 브릿지(대시보드 daySlots 스냅샷) — invalidate로 지우면 프레젠이 빈 칸이 됨 */
  if (key.startsWith('cp.dashboardLessonDaySlots.')) return false;
  if (!keyContainsSchoolIdSegment(key, workspaceId)) return false;
  if (key.startsWith('cp-class-members:')) return true;
  if (key.startsWith('cp.')) return true;
  if (key.startsWith('cp-')) return true;
  return false;
}

function writeStoredSchoolDataVersion(workspaceId: string, version: string | null): void {
  if (!workspaceId || typeof window === 'undefined') return;
  try {
    if (version) {
      window.localStorage.setItem(versionStorageKey(workspaceId), version);
      return;
    }
    window.localStorage.removeItem(versionStorageKey(workspaceId));
  } catch {
    // ignore localStorage failures
  }
}

function setSchoolDataNeedsReload(workspaceId: string, value: boolean): void {
  if (!workspaceId || typeof window === 'undefined') return;
  try {
    if (value) {
      window.localStorage.setItem(needsReloadStorageKey(workspaceId), '1');
      return;
    }
    window.localStorage.removeItem(needsReloadStorageKey(workspaceId));
  } catch {
    // ignore localStorage failures
  }
}

function isSchoolDataNeedsReload(workspaceId: string): boolean {
  if (!workspaceId || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(needsReloadStorageKey(workspaceId)) === '1';
  } catch {
    return false;
  }
}

export function canWriteSchoolScopedDataCache(workspaceId: string): boolean {
  if (!workspaceId || typeof window === 'undefined') return false;
  return !isSchoolDataNeedsReload(workspaceId);
}

/** DB에 방금 쓴 본문을 LS에 시드할 때 invalidate 직후의 needsReload 가드를 해제한다. */
export function allowSchoolScopedCacheSeedAfterInvalidate(workspaceId: string): void {
  setSchoolDataNeedsReload(workspaceId, false);
}

/** LS 캐시 수동 초기화 후 in-memory sync 상태도 리셋 */
export function resetSchoolDataCacheSyncState(workspaceId?: string): void {
  if (workspaceId?.trim()) {
    syncedSchoolIds.delete(workspaceId.trim());
    pendingSyncBySchoolId.delete(workspaceId.trim());
    return;
  }
  syncedSchoolIds.clear();
  pendingSyncBySchoolId.clear();
  /** 로그인 generation마다 마이그레이션 적용 여부를 다시 확인 */
  dataRevisionsColumnMissing = false;
  bumpRevisionsRpcMissing = false;
}

type RemoteSchoolDataState = {
  updatedAt: string | null;
  /** null = DB에 data_revisions 컬럼이 아직 없음(마이그레이션 전) → updated_at 전역 비교로 동작 */
  revisions: SchoolDataRevisions | null;
};

/** 마이그레이션 전 DB: 컬럼·RPC 부재를 세션 동안 기억해 매번 실패 요청을 보내지 않는다 */
let dataRevisionsColumnMissing = false;
let bumpRevisionsRpcMissing = false;

function isMissingColumnError(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  if (error.code === '42703' || error.code === 'PGRST204') return true;
  return /data_revisions/i.test(error.message ?? '') && /does not exist|not find|unknown/i.test(error.message ?? '');
}

function isMissingRpcError(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  if (error.code === 'PGRST202' || error.code === '42883') return true;
  return /bump_workspace_data_revisions/i.test(error.message ?? '') && /not find|does not exist/i.test(error.message ?? '');
}

async function fetchRemoteSchoolDataState(workspaceId: string): Promise<RemoteSchoolDataState> {
  if (!dataRevisionsColumnMissing) {
    const { data, error } = await supabase
      .from('workspaces')
      .select('updated_at, data_revisions')
      .eq('id', workspaceId)
      .maybeSingle();
    if (!error) {
      const row = data as { updated_at?: string | null; data_revisions?: unknown } | null;
      const updatedAt = typeof row?.updated_at === 'string' && row.updated_at ? row.updated_at : null;
      return { updatedAt, revisions: normalizeSchoolDataRevisions(row?.data_revisions) };
    }
    if (!isMissingColumnError(error)) throw new Error(error.message);
    dataRevisionsColumnMissing = true;
  }
  const { data, error } = await supabase.from('workspaces').select('updated_at').eq('id', workspaceId).maybeSingle();
  if (error) throw new Error(error.message);
  const updatedAt = (data as { updated_at?: string | null } | null)?.updated_at;
  return { updatedAt: typeof updatedAt === 'string' && updatedAt ? updatedAt : null, revisions: null };
}

export async function fetchWorkspaceUpdatedAtHead(workspaceId: string): Promise<string | null> {
  return (await fetchRemoteSchoolDataState(workspaceId)).updatedAt;
}

/**
 * domains 미지정 = 워크스페이스 스코프 캐시 전체.
 * 지정 시 해당 파트의 원본 캐시 + 미분류(파생) 캐시만 비운다.
 */
export function clearSchoolScopedDataCaches(workspaceId: string, domains?: ReadonlySet<SchoolDataDomain>): void {
  if (!workspaceId || typeof window === 'undefined') return;
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < deviceCacheStorage.length; index += 1) {
      const key = deviceCacheStorage.key(index);
      if (!key || !isSchoolScopedCacheKey(key, workspaceId)) continue;
      if (domains && !isAffectedBySchoolDataDomains(schoolCacheKeyDomain(key), domains)) continue;
      keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => deviceCacheStorage.removeItem(key));
  } catch {
    // ignore localStorage failures
  }
}

/**
 * 로그아웃: 이 기기에 남은 모든 워크스페이스의 학교 데이터 캐시(학생 명단·기록 등)와 동기화 표시를 지운다.
 * 동기화 표시까지 지워 다음 로그인은 이 기기 첫 동기화처럼 DB에서 다시 받는다.
 */
export function clearAllSchoolScopedDataCachesForSignOut(extraWorkspaceIds: string[] = []): void {
  if (typeof window === 'undefined') return;
  const ids = new Set(extraWorkspaceIds.map((id) => id.trim()).filter(Boolean));
  const metaPrefixes = [
    SCHOOL_DATA_CACHE_VERSION_KEY_PREFIX,
    SCHOOL_DATA_NEEDS_RELOAD_KEY_PREFIX,
    SCHOOL_DATA_REVISIONS_KEY_PREFIX,
  ];
  try {
    const collect = (key: string | null) => {
      if (!key) return;
      const prefix = metaPrefixes.find((p) => key.startsWith(p));
      if (prefix) ids.add(key.slice(prefix.length));
    };
    for (let index = 0; index < window.localStorage.length; index += 1) collect(window.localStorage.key(index));
    for (let index = 0; index < deviceCacheStorage.length; index += 1) collect(deviceCacheStorage.key(index));
    const selected = window.localStorage.getItem('curriculumplanner-school-id');
    if (selected?.trim()) ids.add(selected.trim());
  } catch {
    // ignore storage failures
  }
  ids.forEach((workspaceId) => {
    clearSchoolScopedDataCaches(workspaceId);
    writeStoredSchoolDataVersion(workspaceId, null);
    writeStoredSchoolDataRevisions(workspaceId, null);
    setSchoolDataNeedsReload(workspaceId, false);
  });
  resetSchoolDataCacheSyncState();
}

const ANNUAL_PROJECT_PROGRAMS_CACHE_PREFIX = 'cp.annualProjectPrograms.';

/**
 * 연간 프로젝트 목록 캐시: 지우지 않고(사이드바 빈 화면 방지) `stale` 표시만 한다.
 * 다음 조회가 표시를 보고 DB에서 다시 받아 교체한다. (version을 지우면 "서버 미업로드 로컬"로 오인해 업로드하므로 별도 플래그)
 */
function markAnnualProjectProgramsCachesStale(workspaceId: string): void {
  if (!workspaceId || typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(ANNUAL_PROJECT_PROGRAMS_CACHE_PREFIX) && keyContainsSchoolIdSegment(key, workspaceId)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => {
      const raw = window.localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as unknown;
      /** 레거시 배열 캐시는 version이 없어 어차피 매번 DB와 맞춘다 */
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return;
      const obj = parsed as Record<string, unknown>;
      if (obj.stale === true) return;
      window.localStorage.setItem(key, JSON.stringify({ ...obj, stale: true }));
    });
  } catch {
    // ignore localStorage failures
  }
}

/**
 * 원격 파트별 revision과 로컬 기록을 비교해 바뀐 파트의 캐시만 비운다.
 * - 마이그레이션 전 DB / 이 기기 첫 파트별 동기화: `updated_at` 전역 비교(불일치 시 전체 purge) 후 원격 revision을 기준으로 채택
 * - revision은 같은데 `updated_at`만 다름: revision을 올리지 않는 옛 클라이언트의 저장 → 안전하게 전체 purge
 */
export async function syncSchoolScopedDataCachesDetailed(workspaceId: string): Promise<SchoolDataSyncResult> {
  if (!workspaceId || typeof window === 'undefined') return { isFresh: true, changedDomains: [] };
  if (syncedSchoolIds.has(workspaceId) && !isSchoolDataNeedsReload(workspaceId)) {
    return { isFresh: true, changedDomains: [] };
  }

  const pending = pendingSyncBySchoolId.get(workspaceId);
  if (pending) return pending;

  const task = (async (): Promise<SchoolDataSyncResult> => {
    const remote = await fetchRemoteSchoolDataState(workspaceId);
    const localVersion = readStoredSchoolDataVersion(workspaceId);
    const localRevisions = readStoredSchoolDataRevisions(workspaceId);
    const sameUpdatedAt = isSameSchoolDataVersion(remote.updatedAt, localVersion);

    let result: SchoolDataSyncResult;
    if (remote.revisions == null || localRevisions == null) {
      result = sameUpdatedAt ? { isFresh: true, changedDomains: [] } : { isFresh: false, changedDomains: 'all' };
      /** 로컬에 버전이 없으면(최초 기기·구버전) 프로그램 등 로컬 캐시를 비우지 않고 원격만 기록한다. */
      if (!sameUpdatedAt && localVersion != null) clearSchoolScopedDataCaches(workspaceId);
    } else {
      const stale = computeStaleSchoolDataDomains(localRevisions, remote.revisions);
      if (stale.length > 0) {
        clearSchoolScopedDataCaches(workspaceId, new Set(stale));
        result = { isFresh: false, changedDomains: stale };
      } else if (!sameUpdatedAt && localVersion != null) {
        clearSchoolScopedDataCaches(workspaceId);
        result = { isFresh: false, changedDomains: 'all' };
      } else {
        result = { isFresh: sameUpdatedAt, changedDomains: sameUpdatedAt ? [] : 'all' };
      }
    }

    if (result.changedDomains === 'all' || result.changedDomains.includes('annualProjects')) {
      markAnnualProjectProgramsCachesStale(workspaceId);
    }

    writeStoredSchoolDataVersion(workspaceId, remote.updatedAt);
    if (remote.revisions) writeStoredSchoolDataRevisions(workspaceId, remote.revisions);
    setSchoolDataNeedsReload(workspaceId, false);
    syncedSchoolIds.add(workspaceId);
    pendingSyncBySchoolId.delete(workspaceId);
    return result;
  })().catch((error) => {
    pendingSyncBySchoolId.delete(workspaceId);
    throw error;
  });

  pendingSyncBySchoolId.set(workspaceId, task);
  return task;
}

export async function syncSchoolScopedDataCaches(workspaceId: string): Promise<boolean> {
  return (await syncSchoolScopedDataCachesDetailed(workspaceId)).isFresh;
}

function resolveDomainList(domains: readonly SchoolDataDomain[] | 'all'): SchoolDataDomain[] {
  if (domains === 'all') return [...SCHOOL_DATA_DOMAINS];
  return Array.from(new Set(domains));
}

/** 서버 revision bump. RPC가 없으면(마이그레이션 전) `updated_at`만 올린다 */
async function bumpSchoolDataRevisions(
  workspaceId: string,
  domains: SchoolDataDomain[]
): Promise<SchoolDataBumpResult> {
  if (!bumpRevisionsRpcMissing && domains.length > 0) {
    const { data, error } = await supabase.rpc('bump_workspace_data_revisions', {
      p_workspace_id: workspaceId,
      p_domains: domains,
    });
    if (!error) {
      const payload = (data ?? {}) as { bumped?: unknown; previous?: unknown; updated_at?: unknown };
      const updatedAt = typeof payload.updated_at === 'string' && payload.updated_at ? payload.updated_at : new Date().toISOString();
      return {
        updatedAt,
        bumped: normalizeSchoolDataRevisions(payload.bumped),
        previous: normalizeSchoolDataRevisions(payload.previous),
      };
    }
    if (!isMissingRpcError(error)) throw new Error(error.message);
    bumpRevisionsRpcMissing = true;
  }
  const nextVersion = new Date().toISOString();
  const { error } = await supabase.from('workspaces').update({ updated_at: nextVersion }).eq('id', workspaceId);
  if (error) throw new Error(error.message);
  return { updatedAt: nextVersion, bumped: null, previous: {} };
}

type SchoolDataBumpResult = {
  updatedAt: string;
  /** null = RPC 없음(마이그레이션 전) */
  bumped: SchoolDataRevisions | null;
  previous: SchoolDataRevisions;
};

function recordOwnBump(workspaceId: string, bump: SchoolDataBumpResult): void {
  writeStoredSchoolDataVersion(workspaceId, bump.updatedAt);
  if (bump.bumped) patchStoredSchoolDataRevisions(workspaceId, bump.bumped, bump.previous);
}

/**
 * 학교 데이터 저장 후: 해당 파트 revision bump + 그 파트(와 파생) LS 캐시 purge.
 * 다른 기기는 바뀐 파트만 다시 받는다. 'all'은 여러 파트를 한꺼번에 바꾸는 작업(학년 삭제·팀 가져오기 등)용.
 */
export async function invalidateSchoolScopedDataCaches(
  workspaceId: string,
  domains: readonly SchoolDataDomain[] | 'all' = 'all'
): Promise<void> {
  if (!workspaceId) return;
  const list = resolveDomainList(domains);
  const bump = await bumpSchoolDataRevisions(workspaceId, list);
  recordOwnBump(workspaceId, bump);
  setSchoolDataNeedsReload(workspaceId, true);
  syncedSchoolIds.add(workspaceId);
  pendingSyncBySchoolId.delete(workspaceId);
  clearSchoolScopedDataCaches(workspaceId, domains === 'all' ? undefined : new Set(list));
}

/**
 * 호출부가 이미 캐시를 최신 본문으로 갱신한 저장(프로그램 내용·연간시간표 셀 등): revision만 올리고 LS purge는 하지 않는다.
 */
export async function notifySchoolProgramMutation(
  workspaceId: string,
  domains: readonly SchoolDataDomain[] = ['programContent']
): Promise<void> {
  if (!workspaceId) return;
  const bump = await bumpSchoolDataRevisions(workspaceId, resolveDomainList(domains));
  recordOwnBump(workspaceId, bump);
  setSchoolDataNeedsReload(workspaceId, false);
  syncedSchoolIds.add(workspaceId);
  pendingSyncBySchoolId.delete(workspaceId);
}
